"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.toc = exports.title = exports.slug = exports.position = exports.pageId = exports.navTitle = exports.name = exports.metaDescription = exports.default = void 0;
var _jsxRuntime = require("react/jsx-runtime");
var _react = require("@mdx-js/react");
/*@jsxRuntime automatic @jsxImportSource react*/
// translate-ignore

const pageId = exports.pageId = 29871358695;
const slug = exports.slug = 'guides/api/crm/understanding-the-crm';
// translate-ignore
const navTitle = exports.navTitle = 'Understanding the CRM';
const title = exports.title = 'Understanding the CRM APIs';
const name = exports.name = 'Understanding the CRM APIs';
const metaDescription = exports.metaDescription = 'HubSpot’s CRM is a system for managing customer relationships and data. Learn about CRM objects and the related CRM APIs.';
const position = exports.position = 1;
const toc = exports.toc = [{
  "depth": 0,
  "id": "object-apis",
  "label": "Object APIs",
  "parent": null
}, {
  "depth": 1,
  "id": "object-type-ids",
  "label": "Object type IDs",
  "parent": "object-apis"
}, {
  "depth": 1,
  "id": "unique-identifiers-and-record-ids",
  "label": "Unique identifiers and record IDs",
  "parent": "object-apis"
}, {
  "depth": 0,
  "id": "associations-api",
  "label": "Associations API",
  "parent": null
}, {
  "depth": 0,
  "id": "properties-api",
  "label": "Properties API",
  "parent": null
}, {
  "depth": 0,
  "id": "search-api",
  "label": "Search API",
  "parent": null
}, {
  "depth": 0,
  "id": "pipelines-api",
  "label": "Pipelines API",
  "parent": null
}];
function _createMdxContent(props) {
  const _components = Object.assign({
      h1: "h1",
      p: "p",
      a: "a",
      h2: "h2",
      code: "code",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ul: "ul",
      li: "li",
      em: "em",
      img: "img"
    }, (0, _react.useMDXComponents)(), props.components),
    {
      Alert
    } = _components;
  if (!Alert) _missingMdxReference("Alert", true);
  return (0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [(0, _jsxRuntime.jsx)(_components.h1, {
      children: "Understanding the CRM APIs"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["The foundation of your HubSpot account is a database of your business relationships and processes, called the CRM (Customer Relationship Management). To manage this data, HubSpot accounts include objects, which represent types of relationships or processes. Individual instances of objects, called records, represent the individual entities under each object type (e.g., John Smith is a contact). To store data in each record, you'll use properties (e.g., the email property) and to represent the relationships between individual entities, you can associate records with one another (e.g., associate John Smith with a company Smith & Co.). Further, CRM records can also store information about interactions through associated engagements/activities, such as emails, calls, and meetings. Below, learn about CRM objects, records, properties, associations, pipelines, and searching the CRM. To learn more about managing your CRM database from within HubSpot, check out ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/get-started/manage-your-crm-database",
        children: "HubSpot's Knowledge Base"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h2, {
      children: "Object APIs"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["The object APIs provide access to records and activities. For supported objects, you can use object endpoints and replace the ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "{objectTypeId}"
      }), " in the request URL with the desired object. For example, to create contacts, you'd make a ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "POST"
      }), " request to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "crm/v3/objects/0-1"
      }), " and to create courses, the request would be to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "crm/v3/objects/0-410"
      }), ". Refer to ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/using-object-apis",
        children: "this article"
      }), " for more information about using the object endpoints for various objects."]
    }), "\n", (0, _jsxRuntime.jsx)(Alert, {
      type: "warning",
      titleText: "Please note:",
      children: (0, _jsxRuntime.jsxs)(_components.p, {
        children: ["Some objects have limited API functionality. For more details, click the link to an object's endpoints reference documentation in the table below. If an object listed doesn't have its own doc, you can refer to the ", (0, _jsxRuntime.jsx)(_components.a, {
          href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
          children: "objects API"
        }), " doc and substitute the ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "{objectTypeId}"
        }), " in each endpoint to your desired object."]
      })
    }), "\n", (0, _jsxRuntime.jsx)(_components.h3, {
      children: "Object type IDs"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["When using CRM and other APIs, you'll need to use the ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "objectTypeId"
      }), " field, which is a unique numerical value assigned to each object. For example, to retrieve records, you'd make a ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "GET"
      }), " request to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/objects/{objectTypeId}"
      }), ", or when creating a property for an object, you'd make a ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "POST"
      }), " request to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/properties/{objectTypeId}"
      }), ".The object type ID values are listed in the table below:"]
    }), "\n", (0, _jsxRuntime.jsxs)(_components.table, {
      children: [(0, _jsxRuntime.jsx)(_components.thead, {
        children: (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.th, {
            children: "Type ID"
          }), (0, _jsxRuntime.jsx)(_components.th, {
            children: "Object"
          }), (0, _jsxRuntime.jsx)(_components.th, {
            children: "Description"
          })]
        })
      }), (0, _jsxRuntime.jsxs)(_components.tbody, {
        children: [(0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-2"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Companies"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Stores information about a business or organization. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/companies",
              children: "companies API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-1"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Contacts"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Stores information about an individual person. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/contacts",
              children: "contacts API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-3"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Deals"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent sales opportunities and transactions, tracked through pipeline stages. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/deals",
              children: "deals API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-5"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Tickets"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent customer requests for help or support, tracked through pipeline statuses. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/tickets",
              children: "tickets API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-421"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Appointments"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent encounters or services scheduled for an individual. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
              children: "objects API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-48"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Calls"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents phone call interactions associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/calls",
              children: "calls API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-18"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Communications"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents SMS, LinkedIn, and WhatsApp message interactions associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/communications",
              children: "communications API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-410"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Courses"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent structured programs or series of lessons, trainings, or educational modules. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
              children: "objects API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "2-XXX"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Custom objects"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Stores data that doesn't fit in with existing objects. To find the ", (0, _jsxRuntime.jsx)(_components.code, {
              children: "objectTypeId"
            }), " for a custom object, make a ", (0, _jsxRuntime.jsx)(_components.code, {
              children: "GET"
            }), " request to ", (0, _jsxRuntime.jsx)(_components.code, {
              children: "/crm/v3/schemas"
            }), ". View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
              children: "objects API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-49"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Emails"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents one-to-one email interactions associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/email",
              children: "email API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-19"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Feedback submissions"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Stores information submitted to a feedback survey. Feedback submissions are associated with contact records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/feedback-submissions",
              children: "feedback submissions API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-53"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Invoices"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent the invoices sent for sales transactions. Invoices can be associated with contacts, companies, deals, line items, discounts, fees, and taxes. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/invoices",
              children: "invoices API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-136"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Leads"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent potential customers who have shown interest in your products or services. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/leads",
              children: "leads API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-8"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Line items"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent individual products and services sold in a deal. Line items can be created from existing products in your product library, or can be created standalone. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/line-items",
              children: "line items API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-420"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Listings"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent properties or units to be bought, sold, or rented. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
              children: "objects API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-54"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Marketing events"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent events related to your marketing efforts, specifically including events from connected integrations. You can specify whether or not a contact attended, registered for, or cancelled attending a marketing event. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/marketing/marketing-events",
              children: "marketing events API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-47"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Meetings"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents meeting interactions associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/meetings",
              children: "meetings API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-46"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Notes"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents notes associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/notes",
              children: "notes API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-123"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Orders"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent ecommerce purchases in HubSpot. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/orders",
              children: "orders API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-101"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Payments"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["The payments made by buyers through invoices, payment links, and quotes. Payments can be associated with contacts, companies, deals, invoices, quotes, line items, subscriptions, discounts, fees, and taxes. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/payments",
              children: "payments API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-116"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Postal mail"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents physical mail interactions associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/postal-mail",
              children: "postal mail API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-7"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Products"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent goods or services for sale. Products can't be associated with other objects, but you can create line items based on products and associate those with deals and quotes. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/products",
              children: "products API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-14"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Quotes"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent pricing information shared with potential buyers. Quotes must be ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/guides/api/crm/objects/quotes#adding-associations",
              children: "associated"
            }), " with a deal, line item, and quote template (", (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-64"
            }), "), but can also be associated with contacts, companies, discounts, fees, and taxes. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/quotes",
              children: "quotes API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-162"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Services"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent intangible offerings provided to customers. Examples include onboarding and consulting, repairs and maintenance, and personal care. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "https://developers.hubspot.com/docs/reference/api/crm/objects/objects",
              children: "objects API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-69"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Subscriptions"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent recurring payments scheduled through payment links and quotes. Invoices can be associated with contacts, companies, deals, quotes, line items, payments, discounts, fees, and taxes. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/objects/subscriptions",
              children: "subscriptions API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-27"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Tasks"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["A type of activity that represents to-dos associated with your records. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/crm/engagements/tasks",
              children: "tasks API"
            })]
          })]
        }), (0, _jsxRuntime.jsxs)(_components.tr, {
          children: [(0, _jsxRuntime.jsx)(_components.td, {
            children: (0, _jsxRuntime.jsx)(_components.code, {
              children: "0-115"
            })
          }), (0, _jsxRuntime.jsx)(_components.td, {
            children: "Users"
          }), (0, _jsxRuntime.jsxs)(_components.td, {
            children: ["Represent the users in your HubSpot account. Users cannot be associated with other objects, but can be retrieved and updated via API. View the ", (0, _jsxRuntime.jsx)(_components.a, {
              href: "/reference/api/settings/users/user-details",
              children: "user details API"
            })]
          })]
        })]
      })]
    }), "\n", (0, _jsxRuntime.jsx)(_components.p, {
      children: "You can always use the numerical type ID value, but in some cases, you can also use the object's name for contacts, companies, deals, tickets, or notes. For example:"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.ul, {
      children: ["\n", (0, _jsxRuntime.jsxs)(_components.li, {
        children: ["When starting an import with the ", (0, _jsxRuntime.jsx)(_components.a, {
          href: "/guides/api/crm/imports",
          children: "imports API"
        }), ", the ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "columnObjectTypeId"
        }), " specifies which object the data in your file belongs to. To import data for contacts, your value for ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "columnObjectTypeId"
        }), " could be ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "contact"
        }), " or ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "0-1"
        }), "."]
      }), "\n", (0, _jsxRuntime.jsxs)(_components.li, {
        children: ["When using the ", (0, _jsxRuntime.jsx)(_components.a, {
          href: "/guides/api/crm/associations/associations-v4",
          children: "associations API"
        }), ", the ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "fromObjectType"
        }), " and ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "toObjectType"
        }), " values specify the objects and the direction of the association. To view association types for contacts to companies, your ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "GET"
        }), " request URL could be ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "crm/v4/associations/contact/company/labels"
        }), " or ", (0, _jsxRuntime.jsx)(_components.code, {
          children: "crm/v4/associations/0-1/0-2/labels"
        }), "."]
      }), "\n"]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h3, {
      children: "Unique identifiers and record IDs"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["A unique identifier is a value that differentiates one record in the CRM from another, even if they have otherwise identical information. For example, a database might have records for two people named John Smith. To avoid accidentally sending money to the wrong John Smith, each record is assigned a number as their ", (0, _jsxRuntime.jsx)(_components.em, {
        children: "Record ID"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["When a record is created in HubSpot, its ", (0, _jsxRuntime.jsx)(_components.em, {
        children: "Record ID"
      }), " (", (0, _jsxRuntime.jsx)(_components.code, {
        children: "hs_object_id"
      }), ") is automatically generated and should be treated as a string. Record IDs are unique within an object, so there can be both a contact and company with the same ID. For contacts and companies, there are additional unique identifiers, including a contact's ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "email"
      }), " and a company's ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "domain"
      }), " name. You can also ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://developers.hubspot.com/docs/guides/api/crm/properties#create-unique-identifier-properties",
        children: "create custom unique identifier properties"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["In the CRM APIs, you'll use unique identifier values to identify and manage specific records. You can always use a record's ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "hs_object_id"
      }), " value, but can also use custom unique identifier properties for certain endpoints, specified by the ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "idProperty"
      }), " parameter. For example, to edit a contact, you could make a ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "PATCH"
      }), " request to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/objects/0-1/{contactId}"
      }), " or ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/objects/0-1/{contactEmail}?idProperty=email"
      }), ".Learn more about how HubSpot handles deduplication in the ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/records/deduplication-of-records",
        children: "Knowledge Base"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h2, {
      children: "Associations API"
    }), "\n", (0, _jsxRuntime.jsx)(_components.p, {
      children: "In HubSpot, you can show how objects are related to one another by associating their records. For example, you can associate multiple contacts with a company, and then associate the company and relevant contacts with a deal."
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["When using the ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/associations/associations-v4",
        children: "associations API endpoints"
      }), ", you can substitute objects for ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "{toObjectTypeId}"
      }), " and ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "{fromObjectTypeId}"
      }), " in the request URLs and request bodies. Before associating records across objects, to understand which objects can be associated to one another, you can ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/associations/associations-v4#retrieve-association-labels",
        children: "retrieve association types."
      }), " For example, contacts can be associated with most objects, while Invoices can only be associated with contacts, companies, deals, line items, discounts, fees, and taxes. Depending on your subscription, you can describe the specific relationship types between records using ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/object-settings/create-and-use-association-labels",
        children: "association labels"
      }), ", and your account may have additional custom objects, which can be associated with the other standard objects. Learn more about object relationships and managing associations using the ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/associations/associations-v4",
        children: "associations endpoints"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["If you have access to a HubSpot account, you can also review your account's unique object relationships by navigating to ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/data-management/view-a-model-of-your-crm-object-and-activity-relationships",
        children: "the data model tool."
      }), (0, _jsxRuntime.jsx)(_components.img, {
        src: "https://www.hubspot.com/hubfs/Knowledge_Base_2023/data-model-overview-updated.gif",
        alt: "data-model-overview-updated"
      })]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h2, {
      children: "Properties API"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["Information about records are stored in fields called properties, which are then organized into ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/properties/create-and-edit-properties",
        children: "groups"
      }), ". HubSpot provides a set of default properties for each object. In addition to each object’s default properties, you can store custom data by ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "https://knowledge.hubspot.com/properties/create-and-edit-properties",
        children: "creating custom properties"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["When using the properties API, you can substitute objects in the endpoints to create and manage an object's properties. For example, ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/properties/0-1"
      }), " for contact properties or ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/properties/0-5"
      }), " for ticket properties. Learn more about using the properties API in ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/properties",
        children: "this article"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h2, {
      children: "Search API"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["To filter and sort records and activities based on their properties and associations, you can use the search API. When using the search endpoints, substitute the ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "{objectTypeId}"
      }), " value for the object within which you want to search. For example, to search calls, you'd make a POST request to ", (0, _jsxRuntime.jsx)(_components.code, {
        children: "/crm/v3/objects/0-48/search"
      }), ". Learn more about how to use CRM search API in ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/search",
        children: "this article"
      }), "."]
    }), "\n", (0, _jsxRuntime.jsx)(_components.h2, {
      children: "Pipelines API"
    }), "\n", (0, _jsxRuntime.jsxs)(_components.p, {
      children: ["In HubSpot, you can use pipelines to track records through stages in your processes. For example, you can track deals through a sales processes or tickets through support statuses. Using the pipelines API, you can create, retrieve, edit, and delete pipelines and pipeline stages.Learn which objects have pipelines and how to use pipelines API in ", (0, _jsxRuntime.jsx)(_components.a, {
        href: "/guides/api/crm/pipelines",
        children: "this article"
      }), "."]
    })]
  });
}
function MDXContent(props = {}) {
  const {
    wrapper: MDXLayout
  } = Object.assign({}, (0, _react.useMDXComponents)(), props.components);
  return MDXLayout ? (0, _jsxRuntime.jsx)(MDXLayout, Object.assign({}, props, {
    children: (0, _jsxRuntime.jsx)(_createMdxContent, props)
  })) : _createMdxContent(props);
}
var _default = exports.default = MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}